import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSocket } from '../services/socket';
import { fetchNotifications, fetchUnreadCount, markRead, markAllRead as markAllReadRequest, markUnread, deleteNotifications, deleteAllRead as deleteAllReadRequest } from '../services/notifications';
import { useAuth } from './authContext';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export default function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastNewEventToken, setLastNewEventToken] = useState(0);
  // Notification ids already on screen, so a socket push for one we already fetched is
  // ignored instead of rendering a second identical row and counting twice.
  const seenNotificationIdsRef = useRef(new Set());

  const refreshNotifications = useCallback(async (limitOrOptions = 500, offset = 0, q = "") => {
    if (!isAuthenticated) return;
    const options =
      typeof limitOrOptions === 'object' && limitOrOptions !== null
        ? limitOrOptions
        : { limit: limitOrOptions, offset, q };
    const limitValue = Number(options.limit) || 500;
    const offsetValue = Number(options.offset) || 0;
    const queryValue = typeof options.q === 'string' ? options.q : "";
    const [count, listResponse] = await Promise.all([
      fetchUnreadCount(),
      fetchNotifications({ limit: limitValue, offset: offsetValue, q: queryValue }),
    ]);
    setUnread(count || 0);
    const nextItems = Array.isArray(listResponse?.items) ? listResponse.items : [];
    // Union rather than replace: a notification pushed over the socket may sit outside the
    // page this fetch returned, and forgetting it would let a repeat push duplicate it.
    nextItems.forEach((item) => seenNotificationIdsRef.current.add(String(item?.id)));
    setItems(nextItems);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        await refreshNotifications({ limit: 500, offset: 0 });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated, refreshNotifications]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const s = getSocket();
    /**
     * Add a socket-pushed notification to the list, unless it is already there.
     *
     * The duplicate guard is the point of this function. A push and an HTTP refresh can both
     * deliver the same notification, and they used to land as two rows: the pushed copy had
     * `recipient_id: null` so it keyed on `notifications.id` while the fetched copy keyed on
     * `recipient_id`. One site assignment then rendered as two identical entries and counted
     * twice against the unread badge. The server now sends `recipient_id`, and `id` is
     * matched here so the badge is only incremented for a genuinely new notification.
     *
     * @param {object} payload - Socket payload: id, recipient_id, event_key, title, body, ...
     * @returns {void}
     */
    const handler = (payload) => {
      const key = String(payload.id);
      // Checked against a ref, not `items`: this runs outside React's render cycle, so a
      // state updater's result is not readable here and two pushes arriving back-to-back
      // would both see stale state.
      if (seenNotificationIdsRef.current.has(key)) return;
      seenNotificationIdsRef.current.add(key);

      setItems((prev) => [
        {
          recipient_id: payload.recipient_id ?? null,
          id: payload.id,
          event_key: payload.event_key,
          title: payload.title,
          body: payload.body,
          description: payload.description,
          data: payload.data,
          created_at: payload.created_at,
          read_at: null,
        },
        ...prev,
      ]);
      setUnread((c) => c + 1);
      setLastNewEventToken(Date.now());
    };
    s.on('notifications:new', handler);
    return () => { s.off('notifications:new', handler); };
  }, [isAuthenticated]);

  /**
   * Mark everything read via a single server-side call, then reconcile locally.
   *
   * Previously this gathered recipient_ids out of `items` and POSTed them, but the
   * list endpoint caps a page at 100 rows, so it only ever cleared the loaded page:
   * with 295 unread the badge came back reading 195 while the Unread tab was empty.
   * It also silently skipped socket-pushed items, which carry `recipient_id: null`.
   *
   * @returns {Promise<void>} Resolves once the badge and loaded items reflect the write.
   */
  const markAllRead = useCallback(async () => {
    const { unread: remaining } = await markAllReadRequest();
    // A failed request yields null — leave the badge alone rather than flashing zero.
    if (remaining !== null) setUnread(remaining);
    setItems(prev => prev.map(i => (i.read_at ? i : { ...i, read_at: new Date().toISOString() })));
  }, []);

  const markOneReadLocal = useCallback((recipientId) => {
    if (!recipientId) return;
    setItems((prev) => {
      let changed = false;
      const next = prev.map((i) => {
        if (i.recipient_id !== recipientId) return i;
        if (i.read_at) return i; // already read
        changed = true;
        return { ...i, read_at: new Date().toISOString() };
      });
      if (changed) {
        setUnread((u) => Math.max(0, u - 1));
      }
      return next;
    });
  }, []);

  const markOneUnreadLocal = useCallback((recipientId) => {
    if (!recipientId) return;
    setItems((prev) => {
      let changed = false;
      const next = prev.map((i) => {
        if (i.recipient_id !== recipientId) return i;
        if (!i.read_at) return i; // already unread
        changed = true;
        return { ...i, read_at: null };
      });
      if (changed) {
        setUnread((u) => u + 1);
      }
      return next;
    });
  }, []);

  const markOneRead = useCallback(async (recipientId) => {
    if (!recipientId) return;
    await markRead([recipientId]);
    let changed = false;
    setItems(prev => prev.map(i => {
      if (i.recipient_id === recipientId) {
        if (!i.read_at) changed = true;
        return { ...i, read_at: i.read_at || new Date().toISOString() };
      }
      return i;
    }));
    if (changed) {
      setUnread(u => Math.max(0, u - 1));
    }
  }, []);

  const markOneUnread = useCallback(async (recipientId) => {
    if (!recipientId) return;
    await markUnread([recipientId]);
    let changed = false;
    setItems(prev => prev.map(i => {
      if (i.recipient_id === recipientId) {
        if (i.read_at) changed = true;
        return { ...i, read_at: null };
      }
      return i;
    }));
    if (changed) {
      setUnread(u => u + 1);
    }
  }, []);

  const removeRecipientsLocal = useCallback((recipientIds = []) => {
    if (!recipientIds.length) return;
    setItems(prev => {
      const unreadRemoved = prev.filter(i => recipientIds.includes(i.recipient_id) && !i.read_at).length;
      setUnread(prevUnread => Math.max(0, prevUnread - unreadRemoved));
      return prev.filter(i => !recipientIds.includes(i.recipient_id));
    });
  }, []);



  const deleteRecipients = useCallback(async (recipientIds = []) => {
    if (!recipientIds.length) return;
    await deleteNotifications(recipientIds);
    setItems(prev => {
      const unreadRemoved = prev.filter(i => recipientIds.includes(i.recipient_id) && !i.read_at).length;
      setUnread(prevUnread => Math.max(0, prevUnread - unreadRemoved));
      return prev.filter(i => !recipientIds.includes(i.recipient_id));
    });
  }, []);

  /**
   * Delete every read notification server-side, then reconcile locally.
   *
   * Replaces a localStorage hide-list in NotificationBell that never called the server: the
   * bell filtered rows out by id while the notifications page kept fetching them back, so the
   * two views permanently disagreed and anything "cleared" reappeared on the next refresh.
   * Like markAllRead this sends no ids, because the list endpoint caps a page at 100 rows.
   *
   * @returns {Promise<void>} Resolves once the badge and loaded items reflect the delete.
   */
  const clearAllRead = useCallback(async () => {
    const { unread: remaining } = await deleteAllReadRequest();
    // A failed request yields null — leave the badge alone rather than flashing zero.
    if (remaining !== null) setUnread(remaining);
    setItems(prev => prev.filter(i => !i.read_at));
  }, []);

  const value = useMemo(() => ({
    items,
    unread,
    loading,
    refreshNotifications,
    markAllRead,
    markOneRead,
    markOneUnread,
    markOneReadLocal,
    markOneUnreadLocal,
    removeRecipientsLocal,
    deleteRecipients,
    clearAllRead,
    lastNewEventToken,
    refreshNotifications,
  }), [items, unread, loading, lastNewEventToken, refreshNotifications, markOneRead, markOneUnread, deleteRecipients, clearAllRead]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
